import { useEffect, useRef } from 'react';
import { api } from '@/api/client';

/**
 * Watches a pincode field. When the user types a valid 6-digit pin, resolves the
 * city + state and calls onResolve({city, state, district}) so the form can auto-fill.
 *
 * IMPORTANT: the effect depends ONLY on `pincode`. Callers pass an inline arrow for
 * `onResolve` (new identity every render); if the effect also depended on it, an
 * unrelated re-render right after the fetch started would run the cleanup
 * (cancelled = true) while the `lastQueried` guard blocked a re-fetch — so onResolve
 * never fired and city/state never filled. Holding onResolve in a ref fixes that.
 *
 * Resolution order: our own pincodes master (`/pincodes/lookup/:pin`), then a best-effort
 * fallback to the free India Post API for any pin not in the master.
 */
export function usePincodeAutofill(
  pincode: string | undefined | null,
  onResolve: (data: { city: string; state: string; district?: string }) => void,
) {
  const lastQueried = useRef<string>('');
  const cb = useRef(onResolve);
  cb.current = onResolve; // always the latest callback, without being an effect dependency

  useEffect(() => {
    const p = (pincode || '').toString().trim();
    if (p.length !== 6 || !/^\d{6}$/.test(p)) return;
    if (p === lastQueried.current) return;
    lastQueried.current = p;
    let cancelled = false;

    (async () => {
      // 1) Our masters (authoritative — 28k+ pincodes, matches the States dropdown).
      try {
        const r = await api.get(`/pincodes/lookup/${p}`);
        if (cancelled) return;
        const d: any = r.data ?? {};
        const city = d.city?.name ?? d.cityName ?? (typeof d.city === 'string' ? d.city : '') ?? '';
        const state = d.state?.name ?? d.stateName ?? (typeof d.state === 'string' ? d.state : '') ?? '';
        if (city || state) { cb.current({ city, state, district: d.district }); return; }
      } catch { /* fall through to external lookup */ }

      // 2) Fallback: free India Post lookup for any pincode not in our master.
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 6000);
        const res = await fetch(`https://api.postalpincode.in/pincode/${p}`, { signal: ctrl.signal });
        clearTimeout(timer);
        if (cancelled || !res.ok) return;
        const j: any = await res.json();
        const po = Array.isArray(j) && j[0]?.Status === 'Success' ? j[0]?.PostOffice?.[0] : null;
        if (po) cb.current({ city: po.District || po.Block || po.Name || '', state: po.State || '', district: po.District });
      } catch { /* ignore — manual entry still allowed */ }
    })();

    return () => { cancelled = true; };
  }, [pincode]);
}
